import React, { ElementType, FC, forwardRef } from 'react';
import { Box, BoxProps, Button, ButtonProps, Typography, TypographyProps, useTheme } from '@mui/material';

export interface FooterSectionLinksProps extends BoxProps<ElementType> {
  /**
   * Text wrapped by styled Typography component
   */
  textSlot?: React.ReactNode;
  /**
   * Properties applied to text typography
   */
  textTypographyProps?: TypographyProps<ElementType>;
  /**
   * Alternative to providing button props
   */
  buttonSlot?: React.ReactNode;
  /**
   * Properties applied to button wrapper
   */
  buttonSlotProps?: BoxProps<ElementType>;
  /**
   * Props for button
   */
  buttonProps?: ButtonProps & { label?: React.ReactNode };
  /**
   * Properties applied to link container
   */
  linksWrapperProps?: BoxProps<ElementType>;
}

/**
 * Section for the secondary links, copyright and optional button
 */
export const FooterSectionLinks: FC<FooterSectionLinksProps> = forwardRef(
  (
    {
      sx,
      children,
      className,
      textSlot,
      buttonSlot,
      buttonSlotProps,
      textTypographyProps,
      buttonProps,
      linksWrapperProps,
      ...rest
    },
    ref,
  ) => {
    const { sx: testSx, ...restText } = textTypographyProps ?? {};
    const { sx: linksWrapperSx, ...restLinksWrapper } = linksWrapperProps ?? {};
    const { sx: buttonSlotSx, ...restButtonSlot } = buttonSlotProps ?? {};
    const { label, ...restButton } = buttonProps ?? {};
    const theme = useTheme();

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', lg: 'center' },
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          flexDirection: { xs: 'column', lg: 'row' },
          ...sx,
        }}
        ref={ref}
        data-testid="FooterSectionLinks"
        className={['FooterSectionLinks-root', className].filter(Boolean).join(' ')}
        {...rest}
      >
        {textSlot && (
          <Typography
            variant="disclaimer1"
            sx={{
              my: { xs: 2, lg: 0 },
              color: 'onSurface.os200',
              ...(children && {
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                // separator between text and links
                mr: { lg: 2 },
                '&:after': {
                  content: { xs: 'none', lg: "''" },
                  display: 'block',
                  height: '14px',
                  width: '1px',
                  backgroundColor: 'onSurface.os700',
                  ml: 4,
                },
                // move after the links on mobile
                order: { xs: 1, lg: 0 },
              }),
              ...testSx,
            }}
            data-testid="FooterSectionLinks.text"
            {...restText}
          >
            {textSlot}
          </Typography>
        )}
        {children && (
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', lg: 'center' },
              flexDirection: { xs: 'column', lg: 'row' },
              flexWrap: 'wrap',
              flexGrow: '1',
              gap: { xs: 2, lg: 3 },
              // manipulate style of buttons inside
              '& .MuiButton-root': {
                alignSelf: { xs: 'stretch', lg: 'auto' },
              },
              // manipulates style of links inside
              '& .MuiLink-root': {
                ...theme.typography.disclaimer1,
                p: 0,
                '&, &:hover': {
                  textDecoration: 'underline',
                  color: 'common.white',
                },
              },
              ...linksWrapperSx,
            }}
            data-testid="FooterSectionLinks.content"
            {...restLinksWrapper}
          >
            {children}
          </Box>
        )}
        {(buttonSlot || label) && (
          <Box data-testid="FooterSectionLinks.button" sx={{ order: 2, ...buttonSlotSx }} {...restButtonSlot}>
            {buttonSlot || (
              <Button variant="shortcut" color="inherit" {...restButton}>
                {label}
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  },
);
